import { useState } from 'react';
import type { PaymentMode } from '@dunea/shared';
import { apiGetFunnelStats, type FunnelDetail } from '~/lib/api/funnels';
import { classNames } from '~/utils/classNames';
import { CARD, LoadError, Section, Segmented, SkeletonCard } from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import {
  bumpRates,
  conversionMeaning,
  formatMinor,
  OFFER_STEP_TYPES,
  percentLabel,
  stepTypeLabel,
} from './funnels-model';

export function FunnelStatsView({ appId, detail }: { appId: string; detail: FunnelDetail }) {
  const [mode, setMode] = useState<PaymentMode>(detail.funnel.status === 'active' ? 'live' : 'test');
  const funnelId = detail.funnel.id;
  const stats = useResource(() => apiGetFunnelStats(appId, funnelId, mode), `${appId}:${funnelId}:${mode}`);
  const currency = detail.funnel.currency;
  const offerTitle = new Map(detail.offers.map((o) => [o.id, o.title]));

  const modeSwitch = (
    <Segmented
      label="Stats mode"
      value={mode}
      options={[
        ['test', 'Test'],
        ['live', 'Live'],
      ]}
      onChange={setMode}
    />
  );

  return (
    <Section title="Stats" icon="i-ph:chart-bar" aside={modeSwitch}>
      {stats.loading && !stats.data ? (
        <SkeletonCard lines={4} />
      ) : stats.error ? (
        <LoadError message="Could not load stats." onRetry={() => void stats.reload()} />
      ) : !stats.data?.steps.length ? (
        <div className={classNames(CARD, 'px-4 py-5 text-center')}>
          <p className="text-[12px] text-muted">Add steps to see how visitors move through the funnel.</p>
        </div>
      ) : (
        <>
          <div className={classNames(CARD, 'overflow-hidden')}>
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-hairline text-left">
                  <th className="px-3 py-2 text-[10px] font-medium uppercase tracking-[0.12em] text-muted">Step</th>
                  <th className="px-2 py-2 text-right text-[10px] font-medium uppercase tracking-[0.12em] text-muted">
                    Views
                  </th>
                  <th className="px-2 py-2 text-right text-[10px] font-medium uppercase tracking-[0.12em] text-muted">
                    Converted
                  </th>
                  <th className="px-3 py-2 text-right text-[10px] font-medium uppercase tracking-[0.12em] text-muted">
                    Revenue
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline">
                {stats.data.steps.map((row) => {
                  const isOffer = OFFER_STEP_TYPES.has(row.type);
                  const converted = isOffer ? row.purchases : Math.round(row.conversion * row.views);
                  const bumps = bumpRates(row);

                  return (
                    <tr key={row.stepId} className="align-top">
                      <td className="px-3 py-2.5">
                        <div className="text-primary">{stepTypeLabel(row.type)}</div>
                        <div className="font-geist-mono text-[10px] text-muted">{row.path}</div>
                        {bumps.length > 0 && (
                          <div className="mt-1.5 space-y-0.5">
                            {bumps.map((b) => (
                              <div key={b.offerId} className="text-[11px] text-muted">
                                Bump: {offerTitle.get(b.offerId) ?? 'Offer'}{' '}
                                <span className="font-geist-mono text-secondary">
                                  {b.takes} · {percentLabel(b.rate)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2.5 text-right font-geist-mono text-secondary">{row.views}</td>
                      <td className="px-2 py-2.5 text-right font-geist-mono">
                        <div className="text-primary">{percentLabel(row.conversion)}</div>
                        <div className="text-[10px] text-muted">
                          {converted} {conversionMeaning(row.type)}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right font-geist-mono text-secondary">
                        {row.revenue ? formatMinor(row.revenue, currency) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex items-center justify-between border-t border-hairline px-3 py-2.5">
              <span className="text-[11px] text-muted">Total revenue</span>
              <span className="font-geist-mono text-[13px] text-primary">
                {formatMinor(stats.data.revenue, currency)}
              </span>
            </div>
          </div>
          <p className="text-[11px] leading-relaxed text-muted">
            Views are distinct visitors. Offer steps convert on paid purchases; other steps on reaching the next step.
            Bump take rate is per paid checkout.
          </p>
        </>
      )}
    </Section>
  );
}
